import { Component, computed, inject, input, LOCALE_ID, booleanAttribute } from '@angular/core';
import { TextFieldDisplay } from './text-field-display';
import { SelectDisplay } from './select-display';
import { DatePickerDisplay } from './date-picker-display';
import { LaserTreatmentControlDisplayValue } from './laser-treatment-control-display-value';
import { formatDate } from '@angular/common';
import { LaserTreatmentControlLabels } from './laser-treatment-control-labels';
import { LASER_TREATMENT_CONTROL_MASKS_PARAMS } from './laser-treatment-control-masks';
import { maskitoStringifyNumber } from '@maskito/kit';
import { TextareaAutosizeDirective } from '../shared/textarea-autosize-directive';
import { ScribeSuggestionIndicatorComponent } from '../shared/scribe-suggestion-indicator/scribe-suggestion-indicator.component';
import { AmendmentFieldIndicatorComponent } from '../shared/amendment-field-indicator/amendment-field-indicator.component';
import { ScribeSuggestionMarkerTarget } from '../../scribe-suggestion-marker.model';

@Component({
  selector: 'fb-laser-treatment-control-display',
  imports: [
    TextFieldDisplay,
    DatePickerDisplay,
    SelectDisplay,
    TextareaAutosizeDirective,
    ScribeSuggestionIndicatorComponent,
    AmendmentFieldIndicatorComponent,
  ],
  templateUrl: './laser-treatment-control-display.html',
  styleUrl: './laser-treatment-control-display.scss',
})
export class LaserTreatmentControlDisplay {
  private readonly locale = inject(LOCALE_ID);

  /**
   * Value to be displayed.
   */
  public readonly value = input<LaserTreatmentControlDisplayValue>({
    date: null,
    equipment: null,
    procedureType: null,
    smartCool: null,
    joules: null,
    repetitionRate: null,
    serviceTime: null,
    pulse: null,
    note: null,
    firstLaserOption: null,
    secondLaserOption: null,
  });

  /**
   * Labels for the laser treatment control.
   */
  public readonly labels = input<LaserTreatmentControlLabels>({
    smartCoolText: null,
    joulesText: null,
    pulseText: null,
    repetitionRateText: null,
    firstLaserOptionText: null,
    secondLaserOptionText: null,
  });

  /**
   * Enables muted (grayed-out) visual styling.
   */
  public readonly muted = input(false, { transform: booleanAttribute });

  /**
   * Whether the component is rendered in print mode.
   */
  public readonly isPrinting = input(false, { transform: booleanAttribute });
  public readonly markerTarget = input<ScribeSuggestionMarkerTarget | null>(null);
  /**
   * Index of this treatment row in the widget, for the amendment indicator.
   */
  public readonly rowIndex = input<number | null>(null);

  /**
   * Masking options for control values.
   */
  protected readonly masks = LASER_TREATMENT_CONTROL_MASKS_PARAMS;

  /**
   * Value for the `Date` field.
   */
  protected readonly date = computed(() => {
    const date = this.value().date;

    if (date === null) {
      return '';
    }

    return formatDate(date, 'MM/dd/yyyy', this.locale);
  });

  /**
   * Value for the `Procedure Type` field.
   */
  protected readonly procedureType = computed(() => {
    const procedureType = this.value().procedureType;

    return procedureType ?? '';
  });

  /**
   * Value for the `Equipment` field.
   */
  protected readonly equipment = computed(() => {
    const equipment = this.value().equipment;

    return equipment ?? '';
  });

  /**
   * Value for the `Laser Option 1` field.
   */
  protected readonly firstLaserOption = computed(() => {
    const firstLaserOption = this.value().firstLaserOption;

    return firstLaserOption ?? '';
  });

  /**
   * Value for the `Laser Option 2` field.
   */
  protected readonly secondLaserOption = computed(() => {
    const secondLaserOption = this.value().secondLaserOption;

    return secondLaserOption ?? '';
  });

  /**
   * Value for the `Service Time` field.
   */
  protected readonly serviceTime = computed(() => {
    const serviceTime = this.value().serviceTime;

    return maskitoStringifyNumber(serviceTime, this.masks.serviceTime);
  });

  /**
   * Value for the `Smart Cool` field.
   */
  protected readonly smartCool = computed(() => {
    const smartCool = this.value().smartCool;

    return maskitoStringifyNumber(smartCool, this.masks.smartCool);
  });

  /**
   * Value for the `Joules` field.
   */
  protected readonly joules = computed(() => {
    const joules = this.value().joules;

    return maskitoStringifyNumber(joules, this.masks.joules);
  });

  /**
   * Value for the `Pulse` field.
   */
  protected readonly pulse = computed(() => {
    const pulse = this.value().pulse;

    return maskitoStringifyNumber(pulse, this.masks.pulse);
  });

  /**
   * Value for the `Rep Rate` field.
   */
  protected readonly repetitionRate = computed(() => {
    const repetitionRate = this.value().repetitionRate;

    return maskitoStringifyNumber(repetitionRate, this.masks.repRate);
  });

  /**
   * Value for the `Notes` field.
   */
  protected readonly note = computed(() => {
    const note = this.value().note;

    return note ?? '';
  });

  /**
   * Label for the `Smart Cool` field.
   */
  protected readonly smartCoolLabel = computed(() => {
    const smartCoolText = this.labels().smartCoolText;

    if (smartCoolText === null) {
      return 'Smart Cool';
    }

    return smartCoolText;
  });

  /**
   * Label for the `Joules` field.
   */
  protected readonly joulesLabel = computed(() => {
    const joulesText = this.labels().joulesText;

    if (joulesText === null) {
      return 'Joules';
    }

    return joulesText;
  });

  /**
   * Label for the `Pulse` field.
   */
  protected readonly pulseLabel = computed(() => {
    const pulseText = this.labels().pulseText;

    if (pulseText === null) {
      return 'Pulse';
    }

    return pulseText;
  });

  /**
   * Label for the `Rep Rate` field.
   */
  protected readonly repetitionRateLabel = computed(() => {
    const repetitionRateText = this.labels().repetitionRateText;

    if (repetitionRateText === null) {
      return 'Rep Rate';
    }

    return repetitionRateText;
  });

  /**
   * Label for the `Laser Option 1` field.
   */
  protected readonly firstLaserOptionLabel = computed(() => {
    const firstLaserOptionText = this.labels().firstLaserOptionText;

    if (firstLaserOptionText === null) {
      return 'Laser Option 1';
    }

    return firstLaserOptionText;
  });

  /**
   * Label for the `Laser Option 2` field.
   */
  protected readonly secondLaserOptionLabel = computed(() => {
    const secondLaserOptionText = this.labels().secondLaserOptionText;

    if (secondLaserOptionText === null) {
      return 'Laser Option 2';
    }

    return secondLaserOptionText;
  });
}
